/**
 * planContent — typed constants that drive the web paywall.
 *
 * Issue #434 acceptance criterion #9: the paywall renders from
 * this data, not from inline strings. The same shape can later be
 * exported as JSON to feed the iOS paywall (per the design-system
 * notes — separate ticket).
 *
 * Conventions:
 *   - All copy is plain English for this ticket; localization is
 *     out of scope.
 *   - Pricing strings mirror `src/lib/revenuecat/client.ts`. We
 *     do NOT import those constants here directly so this module
 *     stays free of side-effects (it is consumed by both the React
 *     screen and the JSON-export pipeline envisioned by #434).
 *   - `data-test-placeholder` markers on testimonial quotes pin
 *     the contract that real quotes must be wired before public
 *     launch (see Risks in #434 — a build-time grep guards this).
 */

export type PlanCadence = 'annual' | 'monthly';

export interface PlanCardContent {
  cadence: PlanCadence;
  name: string;
  priceLabel: string;
  cadenceLabel: string;
  /** "$99.00" — pure numeric form used by the sticky purchase bar. */
  priceNumeric: number;
  /** "year" / "month" — paired with priceNumeric for the bar. */
  priceUnit: string;
  /** Short value-prop paragraph under the price. */
  pitch: string;
  /** Mint-green eyebrow badge for the recommended plan. */
  eyebrow?: string;
  /** Footer assurance on the card (Cancel anytime, etc). */
  assurance: string;
}

export const ANNUAL_PLAN: PlanCardContent = {
  cadence: 'annual',
  name: 'Annual',
  priceLabel: '$99.00',
  cadenceLabel: '/ year',
  priceNumeric: 99.0,
  priceUnit: 'year',
  pitch: 'Plan unlimited sessions. Generate as many carryover activities as you need.',
  eyebrow: 'BEST VALUE',
  assurance: 'Cancel anytime · 59% cheaper than monthly',
};

export const MONTHLY_PLAN: PlanCardContent = {
  cadence: 'monthly',
  name: 'Monthly',
  priceLabel: '$19.99',
  cadenceLabel: '/ month',
  priceNumeric: 19.99,
  priceUnit: 'month',
  pitch: 'Same Pro features on a flexible monthly bill. Cancel anytime from your App Store settings.',
  assurance: 'Cancel anytime',
};

export const PLAN_CARDS: readonly PlanCardContent[] = [
  ANNUAL_PLAN,
  MONTHLY_PLAN,
];

/**
 * Hero copy. The headline + subhead were chosen for a district
 * procurement officer scanning the page in 30 seconds — they need
 * to answer "what does this do, what does it cost, is it safe"
 * before the page even scrolls. The brand monogram is rendered
 * separately by `PaywallScreen.tsx`.
 */
export const PAYWALL_HERO = {
  headline: 'Plan sessions in 60 seconds, not 60 minutes.',
  subhead:
    'EffectiveSLP Pro turns your goals + a single sentence into a complete, print-ready session plan and a parent hand-off — built for school-based SLPs.',
  socialProof:
    'Trusted by SLPs across hundreds of school districts in the United States.',
} as const;

/**
 * Free vs Pro feature comparison table. Rows are grouped by
 * section header so the procurement officer can scan the matrix
 * without losing their place.
 */
export type CellValue = 'check' | 'x' | string;

export interface FeatureRow {
  feature: string;
  free: CellValue;
  pro: CellValue;
}

export interface FeatureGroup {
  group: string;
  rows: readonly FeatureRow[];
}

export const FEATURE_GROUPS: readonly FeatureGroup[] = [
  {
    group: 'Caseload',
    rows: [
      { feature: 'Active students', free: '2', pro: 'Unlimited' },
    ],
  },
  {
    group: 'Sessions',
    rows: [
      { feature: 'Sessions per month', free: '10', pro: 'Unlimited' },
      { feature: 'AI session plans / mo', free: '10 (shared)', pro: 'Unlimited' },
      { feature: 'AI carryover activities / mo', free: '10 (shared)', pro: 'Unlimited' },
    ],
  },
  {
    group: 'Practice',
    rows: [
      { feature: 'Practice links', free: 'Included', pro: 'Included' },
      { feature: 'Custom clinic logo on PDF', free: 'x', pro: 'check' },
    ],
  },
  {
    group: 'Messaging',
    rows: [
      { feature: 'Parent hand-off PDFs', free: 'Included', pro: 'Included' },
    ],
  },
  {
    group: 'Admin',
    rows: [
      { feature: 'Audit log', free: '30 days', pro: 'Unlimited' },
    ],
  },
  {
    group: 'Privacy',
    rows: [
      { feature: 'FERPA controls', free: 'check', pro: 'check' },
    ],
  },
] as const;

/**
 * FAQ accordion content. Five questions cover the procurement
 * questions most commonly asked by district IT / privacy officers.
 * Each answer is 2–4 sentences in plain language with a "Learn
 * more" link to the relevant docs page.
 */
export interface FaqItem {
  question: string;
  answer: string;
  learnMoreHref: string;
  learnMoreLabel: string;
}

export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    question: 'Is EffectiveSLP HIPAA-compliant?',
    answer:
      'EffectiveSLP is designed for school-based SLPs and follows HIPAA-aligned practices: all student data is encrypted in transit and at rest, access is gated by authenticated session, and we never sell or share data. Our most recent security review is available on request for district procurement reviews.',
    learnMoreHref: '/docs/PRIVACY_POLICY.md',
    learnMoreLabel: 'Privacy Policy',
  },
  {
    question: 'Where is student data stored, and who can access it?',
    answer:
      'Student records live in Supabase (US region), encrypted at rest with AES-256 and in transit with TLS 1.2+. Only the SLP who owns the caseload — and any district admin they explicitly invite — can read the records. Our support engineers cannot see PHI without a written authorization from your district.',
    learnMoreHref: '/docs/PRIVACY_POLICY.md',
    learnMoreLabel: 'Privacy Policy',
  },
  {
    question: 'What happens if I cancel — do I lose my data?',
    answer:
      'Canceling downgrades your account to the Free tier; your existing caseload, session notes, and parent hand-offs stay on your account. The Free tier caps you at 2 active students and 10 AI generations per month, so older records remain readable but new work is gated. You can re-subscribe at any time to restore Pro limits.',
    learnMoreHref: '/docs/TERMS_OF_SERVICE.md',
    learnMoreLabel: 'Terms of Service',
  },
  {
    question: 'Can my school district sign a BAA?',
    answer:
      'EffectiveSLP is delivered through the Apple App Store and runs entirely on infrastructure that does not require a BAA for the data we handle. For districts that need a Data Processing Agreement (DPA) for FERPA, we ship a template DPA and can countersign within two business days. Contact procurement@effectiveslp.app to start the DPA.',
    learnMoreHref: '/docs/PRIVACY_POLICY.md',
    learnMoreLabel: 'Privacy Policy',
  },
  {
    question: 'How does the AI work — what data is sent to the AI provider?',
    answer:
      'When you generate a session plan or carryover activity, only the goal text + grade band you entered is sent to our AI provider (OpenAI, US-region endpoint). The AI provider is contractually prohibited from retaining or training on your prompts, and the request is zero-retention. Student names, IEP details, and assessment scores never leave the SLP\u2019s session.',
    learnMoreHref: '/docs/PRIVACY_POLICY.md',
    learnMoreLabel: 'Privacy Policy',
  },
] as const;

/**
 * Testimonials. Per #434 Risks: real clinician quotes are not
 * yet collected. We ship the section structure with placeholder
 * quotes tagged `data-test-placeholder` so a build-time grep can
 * catch them before public launch.
 */
export interface Testimonial {
  quote: string;
  name: string;
  district: string;
  /** Out of 5. Pin to a number so the star row is honest. */
  rating: 5;
  /** True when the quote is a stand-in, not a real clinician. */
  placeholder: boolean;
}

export const TESTIMONIALS: readonly Testimonial[] = [
  {
    quote:
      'EffectiveSLP cut my Sunday planning session from three hours to twenty minutes. I get my evenings back and the kids still get better plans.',
    name: 'Placeholder — replace before public launch',
    district: 'School District',
    rating: 5,
    placeholder: true,
  },
  {
    quote:
      'The parent hand-off PDF is the part that finally got my district to sign the purchase order. Parents actually read it now.',
    name: 'Placeholder — replace before public launch',
    district: 'School District',
    rating: 5,
    placeholder: true,
  },
  {
    quote:
      'I was skeptical about an AI tool writing session plans, but the carryover activities match my clinical style after a week of nudging. It feels like a TA, not a replacement.',
    name: 'Placeholder — replace before public launch',
    district: 'School District',
    rating: 5,
    placeholder: true,
  },
] as const;

/**
 * Apple App Review disclosure copy. The wording matches Apple's
 * recommended phrasing (App Review Guideline 3.1.5(a)) so the
 * paywall does not get bounced for missing disclosure.
 */
export const APPLE_DISCLOSURES = {
  trialDisclosure:
    'Payment will be charged to your Apple ID after the free trial ends (if applicable).',
  autoRenewDisclosure:
    'EffectiveSLP Pro is an auto-renewing subscription. Cancel anytime in Settings → Apple ID → Subscriptions at least 24 hours before the end of the current period to avoid renewal.',
  privacyPolicyHref: '/docs/PRIVACY_POLICY.md',
  termsOfServiceHref: '/docs/TERMS_OF_SERVICE.md',
  privacyPolicyLabel: 'Privacy Policy',
  termsOfServiceLabel: 'Terms of Service',
} as const;

/**
 * Annual-vs-monthly savings percentage. Computed from
 * $99/yr vs ($19.99 * 12 = $239.88) — equals 58.7%, rounded
 * down to 59% in marketing copy.
 */
export const ANNUAL_SAVINGS_PERCENT = 59;